// #492 F3: an exercise the model wizard adds to a day for a gap under MEV.
// A choice like a row taken out: the model is rebuilt with it at every change.
// It copies a model row's rule, never numbers of its own (volumeGapProposal.ts).
// Keep free of React and any UI import.
import type { ModelChoices, ModelDayFit } from "./programModelDraft.js";
import { dayRoleOf } from "./programDayRole.js";
import { modelDayText, modelRowEntry, type ModelDay, type ModelRow, type ModelRowWeight, type ProgramModel } from "./programModel.js";
import { fitModelWithOptions, type ModelOptionChoices } from "./programModelOptions.js";
import type { ProgressionRule } from "./programProgression.js";

/** One exercise added to one day of the model. */
export type ModelExtra = { slug: string; dayId: string };

type Model = Omit<ProgramModel, "id">;
type At = Pick<ModelChoices, "level" | "focus" | "exercises">;
type ModelEntry = NonNullable<ModelRow["byLevel"][keyof ModelRow["byLevel"]]>;

/** The row id an extra takes in its day: never a model row's, a slug as the
 *  slot's rowId must be. */
export const modelExtraRowId = (e: ModelExtra): string => `x-${e.dayId}-${e.slug}`.slice(0, 64);

/** The weights an extra copies its rule from, in order. */
const SOURCE_WEIGHTS: readonly ModelRowWeight[] = ["accessory", "main", "optional"];

/** The axes an extra may copy: effort (RPE or RIR, as the row writes it) or
 *  none. % of the MAX and history are fixed values an added exercise lacks. */
const COPIED_AXES: ReadonlySet<ProgressionRule["axis"]> = new Set(["rpe", "rir", "none"]);

/** What an extra on `day` copies, rule and rest: the last accessory at `at`
 *  (its day first), then a main row, then an optional one. A hold copies a
 *  hold. Null: none. */
const sourceFor = (model: Model, day: ModelDay, slug: string, at: At): Pick<ModelEntry, "rule" | "restSec"> | null => {
  const hold = at.exercises?.get(slug)?.isTimeBased === true;
  const days = [day, ...model.days.filter((d) => d.id !== day.id)];
  for (const weight of SOURCE_WEIGHTS) {
    for (const d of days) {
      for (let i = d.rows.length - 1; i >= 0; i -= 1) {
        const got = modelRowEntry(d.rows[i]!, at.level, at.focus);
        if (!got || got.weight !== weight) continue;
        const { rule, restSec } = got.entry;
        if (COPIED_AXES.has(rule.axis) && (rule.work === "countdown") === hold) return { rule, restSec };
      }
    }
  }
  return null;
};

/** The extra as a row of its day, or null when no rule can write it. */
const extraRow = (model: Model, day: ModelDay, e: ModelExtra, at: At): ModelRow | null => {
  const source = sourceFor(model, day, e.slug, at);
  if (!source) return null;
  const byLevel: ModelRow["byLevel"] = {};
  byLevel[at.level] = {
    exercises: [e.slug],
    rule: source.rule,
    ...(source.restSec !== undefined ? { restSec: source.restSec } : {}),
  };
  return { id: modelExtraRowId(e), weight: "accessory", byLevel };
};

/** `model` with each extra at the end of its day, an accessory row at
 *  `at.level` (the fit may cut it like any accessory). An extra no rule can
 *  write, or on a day the model has not, is left out. */
export const modelWithExtras = <M extends Model>(model: M, extras: readonly ModelExtra[], at: At): M => {
  if (extras.length === 0) return model;
  return {
    ...model,
    days: model.days.map((day) => {
      const rows = extras.filter((e) => e.dayId === day.id).flatMap((e) => extraRow(model, day, e, at) ?? []);
      return rows.length === 0 ? day : { ...day, rows: [...day.rows, ...rows] };
    }),
  };
};

/** `model` less the rows the athlete took out, by row id. */
export const modelWithout = <M extends Model>(model: M, removed: ReadonlySet<string>): M =>
  removed.size === 0
    ? model
    : { ...model, days: model.days.map((d) => ({ ...d, rows: d.rows.filter((r) => !removed.has(r.id)) })) };

/** Where an extra goes, or why it does not. `no-room`: the day with the most
 *  time left (`dayId`; null: no day may take one) cannot take it whole. `present`: that day already has
 *  the exercise. `no-rule`: no row of the model can write it. */
export type ModelExtraPlace =
  | { ok: true; extra: ModelExtra }
  | { ok: false; reason: "no-room"; dayId: string | null }
  | { ok: false; reason: "present"; dayId: string }
  | { ok: false; reason: "no-rule" };

/** Where `slug` goes: the day with the fewest fitted `seconds` (one budget
 *  for all), only if the fit keeps it and the day stays in its minutes.
 *  `model`: before `edits`, the options (#479) among them. `fit`: the
 *  current one, when the caller has it. */
export const placeModelExtra = (
  model: Model,
  edits: { extras: readonly ModelExtra[]; removed: ReadonlySet<string>; options?: ModelOptionChoices },
  choices: ModelChoices,
  slug: string,
  fit?: readonly ModelDayFit[],
): ModelExtraPlace => {
  const now =
    fit ?? fitModelWithOptions(modelWithout(modelWithExtras(model, edits.extras, choices), edits.removed), choices, edits.options).days;
  // A deload day (dayRoleOf, any language of its name) never takes an extra.
  const training = (d: ModelDay) =>
    !Object.values(modelDayText(d, choices.focus)).some((name) => typeof name === "string" && dayRoleOf({ name }) === "deload");
  const open = now.filter((f) => model.days.some((d) => d.id === f.dayId && training(d)));
  const roomiest = open.reduce<ModelDayFit | null>((best, d) => (best === null || d.seconds < best.seconds ? d : best), null);
  const day = roomiest ? model.days.find((d) => d.id === roomiest.dayId) : undefined;
  if (!day) return { ok: false, reason: "no-room", dayId: null };
  const extra: ModelExtra = { slug, dayId: day.id };
  const id = modelExtraRowId(extra);
  const present =
    edits.extras.some((e) => modelExtraRowId(e) === id) ||
    day.rows.some((r) => modelRowEntry(r, choices.level, choices.focus)?.entry.exercises.includes(slug) === true);
  if (present) return { ok: false, reason: "present", dayId: day.id };
  if (!extraRow(model, day, extra, choices)) return { ok: false, reason: "no-rule" };
  const tried = modelWithout(modelWithExtras(model, [...edits.extras, extra], choices), edits.removed);
  const after = fitModelWithOptions(tried, choices, edits.options).days.find((d) => d.dayId === day.id);
  return after && after.rowIds.includes(id) && !after.overBudget
    ? { ok: true, extra }
    : { ok: false, reason: "no-room", dayId: day.id };
};
